import { useMemo, useState } from "react";
import { Calendar, dayjsLocalizer } from "react-big-calendar";
import dayjs from "dayjs";
import "react-big-calendar/lib/css/react-big-calendar.css";
import "antd/dist/reset.css";
import {
  onlyTimeFun,
  utcTimeToLocalPA,
} from "../../shared/helpers/DateFunctionDayJs";
import { Card, Flex, Typography } from "antd";
import { toCapitalize } from "../../shared";
import { DraftCustomWeekView } from "./DraftCustomWeekView";
import { DraftCustomDayView } from "./DraftCustomDayView";
import { NavigationControl } from "../appointments/NavigationControl";
import { MoreDraftsDrawer, ViewDraftDetailsModal } from "./modal";
import { useCalendarState } from "../../hooks";

const localizer = dayjsLocalizer(dayjs);

const statusColors = {
  Pending: "#FFA500",
  "Appointment scheduled": "#52c41a",
  "Callback required": "#722ed1",
  Called: "#FFD700",
  "No response": "#1677ff",
  "Not interested": "#f5222d",
};

const { Paragraph } = Typography;

const EventComponent = ({ event }) => {
  const callTime = event?.alldata?.when_to_call_again
    ? utcTimeToLocalPA(event?.alldata?.when_to_call_again)
    : "";

  return (
    <div
      style={{
        fontSize: 12,
        backgroundColor: statusColors[event.status] || "#888",
        borderRadius: 8,
        padding: 6,
        color: "#fff",
        display: "flex",
        flexDirection: "column",
        gap: 6,
        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
      }}
    >
      <Flex vertical gap={3}>
        <div
          style={{
            display: "flex",
            justifyContent: "space-between",
            fontWeight: 600,
          }}
        >
          <Paragraph
            className="m-0 fs-12 text-white"
            ellipsis={{ rows: 1 }}
            style={{ width: 100, textAlign: "left" }}
          >
            {toCapitalize(event.alldata?.client?.name)}
          </Paragraph>
          <span>{callTime}</span>
        </div>
        <Flex gap={2}>
          <Paragraph
            className="m-0 fs-10 text-white"
            ellipsis={{ rows: 1 }}
            style={{
              width: "max-content",
              textAlign: "left",
              padding: "1px 7px",
              borderRadius: 4,
              border: "1px solid #d9d9d9",
            }}
          >
            {toCapitalize(event?.alldata?.services?.[0]?.name)}
          </Paragraph>
          {event?.alldata?.services?.length > 1 && (
            <span
              style={{
                fontSize: 10,
                fontWeight: 500,
                color: "#fff",
                padding: "1px 7px",
                borderRadius: 4,
                border: "1px solid #d9d9d9",
                whiteSpace: "nowrap",
              }}
            >
              +{event?.alldata?.services?.length - 1}
            </span>
          )}
        </Flex>
      </Flex>
    </div>
  );
};

export function DraftCalendarView({ draftData, getDraft }) {
  // ✅ removed startDate/endDate — no longer from hook
  const { date, view, setDate, setView } = useCalendarState();

  const [selectedDraft, setSelectedDraft] = useState(null);
  const [viewmorevisible, setViewMoreVisible] = useState(false);
  const [selecteddraftdate, setSelectedDraftDate] = useState(null);
  const [openViewDraftModal, setOpenViewDraftModal] = useState(false);

  // ✅ removed useEffect for getDraft — parent handles fetching

  const handleEventClick = (event) => {
    setSelectedDraft(event);
    setOpenViewDraftModal(true);
  };

  const handleShowMore = (events, date) => {
    setViewMoreVisible(true);
    setSelectedDraftDate({ data: events, draftDate: date });
  };

  const CustomWeekViewWrapper = useMemo(() => {
    const Wrapper = (props) => (
      <DraftCustomWeekView {...props} handleShowMore={handleShowMore} />
    );
    Wrapper.range = DraftCustomWeekView.range;
    Wrapper.navigate = DraftCustomWeekView.navigate;
    Wrapper.title = DraftCustomWeekView.title;
    return Wrapper;
  }, []);

  return (
    <>
      <Card size="small" className="border-gray no-padding">
        <Flex gap={5} vertical>
          <NavigationControl
            view={view}
            onViewChange={(v) => setView(v)}
            currentDate={date}
            setDate={setDate}
          />
          <Calendar
            localizer={localizer}
            events={draftData}
            startAccessor="start"
            endAccessor="end"
            view={view}
            onView={(v) => setView(v)}
            views={{
              month: true,
              week: CustomWeekViewWrapper,
              day: DraftCustomDayView,
            }}
            date={date}
            onNavigate={setDate}
            onSelectEvent={handleEventClick}
            step={30}
            timeslots={2}
            dayLayoutAlgorithm="no-overlap"
            popup={false}
            doShowMoreDrillDown={false}
            allDayMaxRows={3}
            // onShowMore={handleShowMore}
            drilldownView={null}
            components={{ event: EventComponent }}
            toolbar={false}
            drilldownView={null} // 🚀 THIS disables automatic navigation
            onDrillDown={(date, view) => {}} // optional safety
            onShowMore={(events, date, slot) => {
              handleShowMore(events, date);
              return false; // 🚀 IMPORTANT: stop default navigation
            }}
            className={`my-calendar ${view === "month" ? "h-700" : ""}`}
          />
        </Flex>
      </Card>

      <ViewDraftDetailsModal
        open={openViewDraftModal}
        selectedDraft={selectedDraft}
        onClose={() => setOpenViewDraftModal(false)}
        call={() => getDraft()}
      />

      <MoreDraftsDrawer
        visible={viewmorevisible}
        selecteddraftdate={selecteddraftdate}
        handleEventClick={handleEventClick}
        onClose={() => setViewMoreVisible(false)}
      />
    </>
  );
}
